// src/pages/WhatsNextPage.js - WITH FIXED ALL CATEGORIES FILTER
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import EventModal from '../components/EventModal';
import { EventCardSkeleton } from '../components/SkeletonLoader';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/WhatsNextPage.css';

const WhatsNextPage = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Pagination state
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Ref for intersection observer
  const observerTarget = useRef(null);

  // Fetch categories for filter
  const fetchCategories = async () => {
    try {
      const response = await api.getEventCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchEvents(1);

    return () => {
      setLoading(true);
      setEvents([]);
      setPage(1);
      setHasMore(true);
    };
  }, [location.pathname]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && hasMore) {
          loadMoreEvents();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loading, loadingMore, hasMore, page]);

  const fetchEvents = async (pageNum, category = selectedCategory, search = searchQuery) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      const response = await api.getEvents({
        page: pageNum,
        limit: 6,
        category_id: category || null, // Empty string becomes null
        search: search || null
      });

      if (response.success && response.data) {
        setEvents(prev => {
          if (pageNum === 1) {
            return response.data;
          }
          // Filter out duplicates based on event ID
          const existingIds = new Set(prev.map(e => e.id));
          const newEvents = response.data.filter(e => !existingIds.has(e.id));
          return [...prev, ...newEvents];
        });
        setHasMore(response.pagination?.has_more || false);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreEvents = () => {
    if (hasMore && !loadingMore) {
      fetchEvents(page + 1, selectedCategory, searchQuery);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setEvents([]);
    setPage(1);
    setHasMore(true);
    fetchEvents(1, selectedCategory, searchQuery);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setEvents([]);
    setPage(1);
    setHasMore(true);
    setTimeout(() => fetchEvents(1, category, searchQuery), 100);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^\S+@\S+$/i.test(subscribeEmail)) {
      toast.error('Invalid email address');
      return;
    }

    try {
      const response = await api.subscribeNewsletter(subscribeEmail);
      if (response.success) {
        toast.success('Successfully subscribed!');
        setSubscribeEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <PageWrapper>
      <SEO
        title="On the Horizon - Upcoming Events | Supernova Lab"
        description="Stay ahead of what's next from bold missions to mastery sessions to our ever-evolving experimental pipeline"
        keywords="events, workshops, upcoming, Barbados, innovation"
      />

      <main className="whats-next-page">
        <motion.section
          className="whats-next-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <h1 className="whats-next-main-title">On the Horizon</h1>
            <p className="whats-next-subtitle">
              Stay ahead of what's next from bold missions to mastery<br />
              sessions to our ever-evolving experimental pipeline
            </p>
          </div>
        </motion.section>

        <motion.section
          className="whats-next-search-filter-section py-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="container d-flex justify-content-start align-items-center gap-3">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '1rem', flex: 1 }}>
              <input
                type="text"
                className="whats-next-search-input"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="event-search-btn">Search</button>
            </form>
            <select
              className="form-select whats-next-filter-btn"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              style={{ width: '200px', cursor: 'pointer' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </motion.section>

        <section className="events-grid-section">
          <div className="container">
            {loading && events.length === 0 ? (
              <div className="row g-4">
                {Array(6).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-4 col-md-6">
                    <EventCardSkeleton />
                  </div>
                ))}
              </div>
            ) : events.length > 0 ? (
              <>
                <div className="row g-4">
                  {events.map((event) => (
                    <div key={event.id} className="col-lg-4 col-md-6">
                      <div className="event-card">
                        <div className="event-image-wrapper">
                          <SafeImage
                            src={event.image}
                            alt={event.title}
                            className="event-image"
                            width={400}
                            height={250}
                            placeholderText="Event"
                          />
                          <div className="event-badges">
                            <span className="event-category-badge">{event.category_name}</span>
                            <span className="event-price-badge">
                              {event.price === 'Free' ? 'Free Event' : `Price: ${event.price}`}
                            </span>
                          </div>
                        </div>
                        <div className="event-card-body">
                          <h3 className="event-title">{event.title}</h3>
                          <p className="event-description">
                            {event.description.length > 100 ? event.description.substring(0, 60) + '...' : event.description}
                          </p>
                          <div className="event-meta">
                            <p className="event-location">{event.location}</p>
                            <div className="event-datetime">
                              <span className="event-date">{formatDate(event.date)}</span>
                              <span className="event-time">{event.time}</span>
                            </div>
                          </div>
                          <button className="event-btn" onClick={() => handleEventClick(event)}>
                            {event.button_text || 'Register Now'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Loading More Skeleton */}
                {loadingMore && (
                  <div className="row g-4 mt-1">
                    {Array(3).fill(0).map((_, index) => (
                      <div key={`loading-${index}`} className="col-lg-4 col-md-6">
                        <EventCardSkeleton />
                      </div>
                    ))}
                  </div>
                )}

                {/* Intersection Observer Target */}
                <div ref={observerTarget} style={{ height: '20px' }} />
              </>
            ) : (
              <div className="col-12 text-center py-5">
                <p className="text-muted">No events available at the moment</p>
              </div>
            )}
          </div>
        </section>

        <section className="newsletter-section-alt">
          <div className="container">
            <div className="text-center">
              <h2 className="newsletter-title-alt mb-3">Stay Informed with Supernova</h2>
              <p className="newsletter-subtitle-alt mb-4">Subscribe to our newsletter for the latest updates.</p>
              <form onSubmit={handleSubscribe} className="newsletter-form-alt">
                <div className="input-group-alt">
                  <input
                    type="email"
                    className="form-control newsletter-input-alt"
                    placeholder="Enter your email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                  />
                  <button className="newsletter-btn-alt" type="submit">Subscribe</button>
                </div>
              </form>
            </div>
          </div>
        </section>

        <EventModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          event={selectedEvent}
        />
      </main>
    </PageWrapper>
  );
};

export default WhatsNextPage;